import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { stripeService } from '@/services/stripe';
import type { SubscriptionStatus } from '@/types/subscription';
import type { UserPlan } from '@/utils/planLimits';

type SubscriptionData = {
  plan: UserPlan;
  subscriptionStatus?: SubscriptionStatus;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
  stripeCustomerId?: string;
};

/**
 * Hook for managing user subscription state and actions
 */
export const useSubscription = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [upgrading, setUpgrading] = useState(false);
  const [managing, setManaging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['subscription', user?.uid],
    queryFn: async (): Promise<SubscriptionData> => {
      if (!user?.uid) {
        return { plan: 'free' };
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        return { plan: 'free' };
      }

      const data = userDoc.data();

      return {
        plan: data.plan || 'free',
        subscriptionStatus: data.subscriptionStatus,
        currentPeriodEnd: data.currentPeriodEnd?.toDate(),
        cancelAtPeriodEnd: data.cancelAtPeriodEnd,
        stripeCustomerId: data.stripeCustomerId,
      };
    },
    enabled: !!user?.uid,
    staleTime: 60 * 1000, // 1 minute
  });

  /**
   * Start upgrade flow - redirects to Stripe Checkout
   */
  const upgrade = async () => {
    setUpgrading(true);
    setError(null);

    try {
      await stripeService.createCheckoutSession();
      // User will be redirected to Stripe, so this won't return
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout';
      setError(message);
      setUpgrading(false);
      throw err;
    }
  };

  /**
   * Open subscription management portal
   */
  const manageSubscription = async () => {
    setManaging(true);
    setError(null);

    try {
      await stripeService.openCustomerPortal();
      // User will be redirected to Stripe Portal
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open portal';
      setError(message);
      setManaging(false);
      throw err;
    }
  };

  /**
   * Refresh subscription data (after returning from Stripe)
   */
  const refreshSubscription = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
    queryClient.invalidateQueries({ queryKey: ['userPlan'] });
  };

  // Derived state
  const isPro = query.data?.plan === 'pro';
  const isCanceling = query.data?.cancelAtPeriodEnd === true;
  const isPastDue = query.data?.subscriptionStatus === 'past_due';

  return {
    // Query data
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,

    // Derived state
    isPro,
    isCanceling,
    isPastDue,

    // Actions
    upgrade,
    manageSubscription,
    refreshSubscription,

    // Action state
    upgrading,
    managing,
    error,
  };
};
